import {
  useQueries,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { useInitDlns } from '~/models/deliveries/DeliveryNote/utils/hooks';

import { promiseAllThrottled } from '~/utils/promise';

import {
  clearDeliveryNoteRowCache,
  mapDeliveryRow,
} from '~/components/deliveries/utils';

import { queryKeysDeliveryNote } from './queryKeys';
import {
  type DeliveryNoteListItem,
  type DeliveryNoteSearchParams,
} from './types';
import { fetchDeliveryNoteSearchCount } from './useQueryDeliveryNoteSearchCount';
import { zodFilteredDeliveryNotesPayloadSchema } from './zodDeliveryNoteFiltersSchema';

type DeliveryNotesResponse = {
  assets: DeliveryNoteListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  sequenceNumber: string;
  totalCount: number;
};

export const zodDeliveryNotesPayloadSchema =
  zodFilteredDeliveryNotesPayloadSchema.extend({
    includeChains: z.boolean().optional().prefault(false),
    modifiedAfter: z.string().optional().prefault('0'),
  });

/**
 * Fetches all delivery notes based on search parameters from the API.
 *
 * @param {DeliveryNoteSearchParams} searchParams - The search parameters for filtering delivery notes
 * @returns {Promise<DeliveryNotesResponse>} The matching delivery notes data
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/search_delivery_note_asset_delivery_note_search_post
 */
export const fetchDeliveryNotes = async (
  searchParams: DeliveryNoteSearchParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.GET_ALL(), {
      json: searchParams,
      validate: {
        body: zodDeliveryNotesPayloadSchema,
      },
      ...options,
    })
    .json<DeliveryNotesResponse>();

  return response;
};

/**
 * React Query based custom hook for getting the data for all delivery notes with given search parameters.
 * Handles pagination.
 *
 * @param {DeliveryNoteSearchParams} searchParams - The search parameters for filtering delivery notes
 * @param {Object} options - Additional options for the useQuery hook
 * @returns {UseQueryResult<number>} The result of the useQuery hook containing the count
 */
export const useQueryDeliveryNotes = (
  searchParams: DeliveryNoteSearchParams,
  options?: Omit<UseQueryOptions<number>, 'queryKey' | 'queryFn'>,
  shouldUsePrefetching = false,
) => {
  const initDlns = useInitDlns();
  const previousDataRef = useRef<
    | {
        deliveryNotes: Awaited<ReturnType<typeof fetchDeliveryNotes>> & {
          rows: any[];
        };
        count: number | undefined;
      }
    | undefined
  >(undefined);

  const queryClient = useQueryClient();

  // FIXME VGS-7376: remove useEffect in DeliveryList page once useQueryDeliveryNotes only consists of a single request

  const getDLNQuery = useCallback(
    (customOffset?: number) => ({
      enabled: options?.enabled !== false,
      gcTime: ms('30s'),
      placeholderData: () => previousDataRef.current?.deliveryNotes,
      async queryFn({ signal }: { signal: AbortSignal }) {
        const response = await fetchDeliveryNotes(
          {
            ...searchParams,
            offset: customOffset ?? searchParams.offset,
          },
          {
            signal,
          },
        );
        const initializedAssets = response.assets?.length
          ? await initDlns(response.assets)
          : [];

        const rows = initializedAssets.map((deliveryNote) =>
          mapDeliveryRow(deliveryNote),
        );

        const result = {
          ...response,
          rows,
        };

        previousDataRef.current = {
          ...previousDataRef.current,
          deliveryNotes: result,
        };

        return result;
      },
      queryKey: queryKeysDeliveryNote.getAll({
        ...searchParams,
        offset: customOffset ?? searchParams.offset,
      }),
      staleTime: ms('10s'),
    }),
    [searchParams, options?.enabled],
  );

  const getDLNCountQuery = useCallback(() => {
    const countQueryParams = {
      filterConfig: searchParams?.filterConfig,
    };
    return {
      enabled: options?.enabled !== false,
      placeholderData: () => previousDataRef.current?.count,
      async queryFn({ signal }: { signal: AbortSignal }) {
        const count = await fetchDeliveryNoteSearchCount(countQueryParams, {
          signal,
        });

        previousDataRef.current = {
          ...previousDataRef.current,
          count,
        };

        return count;
      },
      queryKey: queryKeysDeliveryNote.getSearchCount(countQueryParams),
    };
  }, [searchParams?.filterConfig, options?.enabled]);

  const queries = useQueries({
    queries: [getDLNQuery(), getDLNCountQuery()],
  });

  const [deliveryNotesQuery, deliveryNotesCountQuery] = queries;

  const isLoading = queries.some(({ isLoading }) => isLoading);
  const isFetching = queries.some(({ isFetching }) => isFetching);
  const isError = queries.some(({ isError }) => isError);
  const isSuccess = queries.every(({ isSuccess }) => isSuccess);

  const data = {
    hasNextPage: deliveryNotesQuery.data?.hasNextPage,
    paginatedCount: deliveryNotesQuery.data?.paginatedCount,
    rows: deliveryNotesQuery.data?.rows ?? [],
    sequenceNumber: deliveryNotesQuery.data?.sequenceNumber,
    totalCount: deliveryNotesCountQuery.data,
  };

  const refetch = async () => {
    clearDeliveryNoteRowCache();
    await promiseAllThrottled(queries.map(async (query) => query.refetch()));
  };

  useEffect(() => {
    if (shouldUsePrefetching) {
      const { limit, offset } =
        zodDeliveryNotesPayloadSchema.parse(searchParams);

      const totalCount = Number(data?.totalCount);

      if (!Number.isFinite(totalCount)) {
        return;
      }

      // Prefetch next page if there are more items
      if (offset + limit < totalCount) {
        void queryClient.prefetchQuery(getDLNQuery(offset + limit));
      }

      // Prefetch previous page if we're not at the start
      if (offset - limit >= 0) {
        void queryClient.prefetchQuery(getDLNQuery(offset - limit));
      }
    }
  }, [searchParams.limit, searchParams.offset, data?.totalCount]);

  return {
    data,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  };
};
